import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useUpdateCtaTranslationMutation } from '~/features/dictionaries/model/api/cta-dictionary-api';

type TranslationRow = {
  dictionary_item_id: number;
  language_code: string;
  language_name: string;
  cta_key: string;
  cta: string;
  translation: string;
};

type CtaTranslationModalProps = {
  open: boolean;
  handleClose: () => void;
  translationRow?: TranslationRow | null;
  onSaved?: () => void;
};

export const CtaTranslationModal = ({
  open,
  handleClose,
  translationRow,
  onSaved,
}: CtaTranslationModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState('');
  const [updateTranslation, { isLoading }] = useUpdateCtaTranslationMutation();

  useEffect(() => {
    setText(translationRow?.translation ?? '');
  }, [translationRow, open]);

  const handleSave = async () => {
    if (!translationRow) {
      return;
    }

    if (!text.trim()) {
      enqueueSnackbar('Введите текст перевода CTA.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }

    try {
      await updateTranslation({
        itemId: translationRow.dictionary_item_id,
        languageCode: translationRow.language_code,
        text: text.trim(),
      }).unwrap();

      enqueueSnackbar('Перевод CTA обновлен.', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      handleClose();
      onSaved?.();
    } catch (error: any) {
      const validationErrors = error?.data?.errors;
      const validationMessage = validationErrors
        ? Object.values(validationErrors).flat().join('\n')
        : error?.data?.message || 'Не удалось обновить перевод CTA.';

      enqueueSnackbar(validationMessage, {
        variant: 'error',
        autoHideDuration: 5000,
        style: { maxWidth: '700px', whiteSpace: 'pre-wrap' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          width: 620,
          maxWidth: '95vw',
          p: 3,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">Редактировать перевод CTA</Typography>

          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              Язык
            </Typography>
            <TextField value={translationRow?.language_name ?? ''} disabled fullWidth />
          </Box>
          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              CTA ключ
            </Typography>
            <TextField value={translationRow?.cta_key ?? ''} disabled fullWidth />
          </Box>
          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              CTA по умолчанию
            </Typography>
            <TextField value={translationRow?.cta ?? ''} disabled fullWidth />
          </Box>
          <Box>
            <Typography fontWeight={600} variant="subtitle2" sx={{ mb: 0.5 }}>
              Перевод
            </Typography>
            <TextField
              value={text}
              onChange={e => setText(e.target.value)}
              disabled={isLoading}
              autoFocus
              fullWidth
            />
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" onClick={handleClose} disabled={isLoading}>
              Отмена
            </Button>
            <Button fullWidth variant="contained" onClick={handleSave} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> : null}
              Сохранить
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
